// src/pages/checkout.jsx
import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useCart } from "../CartContext";
import TopSection from "../components/topSection";
import { getData, postData } from "../api"; // axios helper (baseURL=/api/v1)
import BG from "../assets/shop_bg.png";

const formatLKR = (n) =>
  `Rs. ${Number(n || 0).toLocaleString("en-LK", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

// Build and submit a plain HTML form (POST) to Webxpay
function submitWebxpayForm(actionUrl, fields) {
  const form = document.createElement("form");
  form.method = "POST";
  form.action = actionUrl;
  form.style.display = "none";
  Object.entries(fields || {}).forEach(([k, v]) => {
    const input = document.createElement("input");
    input.type = "hidden";
    input.name = k;
    input.value = v == null ? "" : String(v);
    form.appendChild(input);
  });
  document.body.appendChild(form);
  form.submit();
}

export default function CheckoutPage() {
  const navigate = useNavigate();
  const { cart, subtotal } = useCart();

  // ---- Form state ----
  const [form, setForm] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    country: "",
    state: "",
    city: "",
    address1: "",
    address2: "",
    zip: "",
  });

  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);

  // ---- Shipping (live from API) ----
  const [shipping, setShipping] = useState(0);
  const [shippingRule, setShippingRule] = useState("");
  const [shipLoading, setShipLoading] = useState(false);
  const [shipError, setShipError] = useState("");

  const subTotalAmount = useMemo(() => Number(subtotal || 0), [subtotal]);
  const grandTotal = useMemo(
    () => subTotalAmount + Number(shipping || 0),
    [subTotalAmount, shipping]
  );

  // Quote shipping when country + city available
  useEffect(() => {
    const { country, city } = form;
    if (!country.trim() || !city.trim()) {
      setShipping(0);
      setShippingRule("");
      return;
    }
    let ignore = false;
    (async () => {
      try {
        setShipError("");
        setShipLoading(true);
        const qs = `country=${encodeURIComponent(
          country.trim()
        )}&city=${encodeURIComponent(city.trim())}`;
        const data = await getData(`shipping/quote?${qs}`); // {currency, amount, rule}
        if (ignore) return;
        setShipping(Number(data?.amount ?? 0));
        setShippingRule(String(data?.rule ?? ""));
      } catch {
        if (ignore) return;
        setShipError("Could not get a shipping quote. Please check country/city.");
        setShipping(0);
        setShippingRule("");
      } finally {
        if (!ignore) setShipLoading(false);
      }
    })();
    return () => {
      ignore = true;
    };
  }, [form.country, form.city]);

  const onChange = (e) => {
    const { name, value } = e.target;
    setForm((f) => ({ ...f, [name]: value }));
  };

  // Validate contact + shipping fields for new API
  const validate = () => {
    const e = {};
    if (!form.firstName.trim()) e.firstName = "Required";
    if (!form.lastName.trim()) e.lastName = "Required";
    if (!/^\S+@\S+\.\S+$/.test(form.email)) e.email = "Enter a valid email";
    if (!form.phone.trim()) e.phone = "Required";
    if (!form.country) e.country = "Required";
    if (!form.city) e.city = "Required";
    if (!form.address1) e.address1 = "Required";
    if (!form.zip) e.zip = "Required";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  // Submit: create order -> start Webxpay -> auto-POST to actionUrl
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    if (!cart?.length) return;

    // Build EXACT payload the new /v1/orders expects
    const payload = {
      customerFirstName: form.firstName.trim(),
      customerLastName: form.lastName.trim(),
      customerEmail: form.email.trim(),
      items: cart.map((it) => ({
        sku: String(it.sku || it.id || it._id || it.title || "").trim(),
        qty: Number(it.qty ?? 1),
      })),
      shipping: {
        firstName: form.firstName.trim(),
        lastName: form.lastName.trim(),
        phone: form.phone.trim(),
        address1: form.address1.trim(),
        address2: (form.address2 || "").trim(),
        city: form.city.trim(),
        state: (form.state || "").trim(),
        postalCode: form.zip.trim(),
        country: form.country.trim(),
      },
      currency: "LKR",
    };

    try {
      setSubmitting(true);

      // 1) Create order
      const order = await postData("orders", payload);
      const oid = order?.id || order?._id || order?.orderNo;
      if (!oid) throw new Error("Order id missing in response");

      // 2) Start Webxpay payment session
      const webx = await postData(`payments/webxpay/orders/${oid}/start`, {});
      const actionUrl = webx?.actionUrl;
      const fields = webx?.fields;
      if (!actionUrl || !fields) throw new Error("Webxpay start response missing actionUrl/fields.");

      // 3) Redirect by auto-submitting the POST form
      submitWebxpayForm(actionUrl, fields);
      // (navigation happens here)
    } catch (err) {
      console.error(err);
      const apiMsg =
        err?.response?.data?.message ??
        err?.response?.data?.error ??
        err?.message ??
        "Payment failed";
      alert(Array.isArray(apiMsg) ? apiMsg.join(", ") : apiMsg);
    } finally {
      setSubmitting(false);
    }
  };

  if (!cart?.length) {
    return (
      <div className="container py-5">
        <h2 className="mb-3">Checkout</h2>
        <p>Your cart is empty.</p>
        <Link className="btn btn-outline-secondary" to="/shop">
          Back to Shop
        </Link>
      </div>
    );
  }

  return (
    <>
      <TopSection text="Checkout" image={BG} backgroundPositionY={"0%"} backgroundSize={"contain"} />

      <div className="container py-5">
        <div className="my-3 me-auto ">
          <Link className="btn btn-outline-secondary" to="/cart">
            Back to Cart
          </Link>
        </div>

        <div className="row g-5">
          {/* Left: Contact + Shipping */}
          <div className="col-lg-5 col-12 me-auto">
            <form id="checkout-form" onSubmit={handleSubmit} className="checkout-form">
              <h5 className="mb-3 fw-bold text-uppercase">Contact</h5>

              <div className="row g-3">
                <div className="col-sm-6">
                  <label className="form-label">First Name</label>
                  <input
                    name="firstName"
                    className={`form-control ${errors.firstName ? "is-invalid" : ""}`}
                    placeholder="Enter first name"
                    value={form.firstName}
                    onChange={onChange}
                  />
                  {errors.firstName && <div className="invalid-feedback">{errors.firstName}</div>}
                </div>
                <div className="col-sm-6">
                  <label className="form-label">Last Name</label>
                  <input
                    name="lastName"
                    className={`form-control ${errors.lastName ? "is-invalid" : ""}`}
                    placeholder="Enter last name"
                    value={form.lastName}
                    onChange={onChange}
                  />
                  {errors.lastName && <div className="invalid-feedback">{errors.lastName}</div>}
                </div>
                <div className="col-12">
                  <label className="form-label">Email</label>
                  <input
                    type="email"
                    name="email"
                    className={`form-control ${errors.email ? "is-invalid" : ""}`}
                    placeholder="name@example.com"
                    value={form.email}
                    onChange={onChange}
                  />
                  {errors.email && <div className="invalid-feedback">{errors.email}</div>}
                </div>
                <div className="col-12">
                  <label className="form-label">Phone</label>
                  <input
                    type="tel"
                    name="phone"
                    className={`form-control ${errors.phone ? "is-invalid" : ""}`}
                    placeholder="+94 7X XXX XXXX"
                    value={form.phone}
                    onChange={onChange}
                  />
                  {errors.phone && <div className="invalid-feedback">{errors.phone}</div>}
                </div>
              </div>

              <hr className="my-4" />

              <h5 className="mb-3 fw-bold text-uppercase">Shipping Address</h5>

              <div className="row g-3">
                <div className="col-12">
                  <label className="form-label">Country</label>
                  <select
                    name="country"
                    className={`form-select ${errors.country ? "is-invalid" : ""}`}
                    value={form.country}
                    onChange={onChange}
                  >
                    <option value="">Select country</option>
                    <option value="Sri Lanka">Sri Lanka</option>
                    <option value="India">India</option>
                    <option value="United Kingdom">United Kingdom</option>
                    <option value="United States">United States</option>
                  </select>
                  {errors.country && <div className="invalid-feedback">{errors.country}</div>}
                </div>

                <div className="col-12">
                  <label className="form-label">Address 1</label>
                  <input
                    type="text"
                    name="address1"
                    className={`form-control ${errors.address1 ? "is-invalid" : ""}`}
                    placeholder="Street address"
                    value={form.address1}
                    onChange={onChange}
                  />
                  {errors.address1 && <div className="invalid-feedback">{errors.address1}</div>}
                </div>

                <div className="col-12">
                  <label className="form-label">Address 2 (optional)</label>
                  <input
                    type="text"
                    name="address2"
                    className="form-control"
                    placeholder="Apartment, suite, etc."
                    value={form.address2}
                    onChange={onChange}
                  />
                </div>

                <div className="col-sm-6">
                  <label className="form-label">City</label>
                  <input
                    type="text"
                    name="city"
                    className={`form-control ${errors.city ? "is-invalid" : ""}`}
                    placeholder="Colombo"
                    value={form.city}
                    onChange={onChange}
                  />
                  {errors.city && <div className="invalid-feedback">{errors.city}</div>}
                </div>

                <div className="col-sm-6">
                  <label className="form-label">State (optional)</label>
                  <input
                    type="text"
                    name="state"
                    className="form-control"
                    placeholder="State/Province"
                    value={form.state}
                    onChange={onChange}
                  />
                </div>

                <div className="col-sm-6">
                  <label className="form-label">Zip/Postal Code</label>
                  <input
                    type="text"
                    name="zip"
                    className={`form-control ${errors.zip ? "is-invalid" : ""}`}
                    placeholder="10100"
                    value={form.zip}
                    onChange={onChange}
                  />
                  {errors.zip && <div className="invalid-feedback">{errors.zip}</div>}
                </div>
              </div>

              {shipError && <div className="alert alert-warning mt-3 py-2">{shipError}</div>}
            </form>
          </div>

          {/* Right: Order Summary */}
          <div className="col-lg-5 col-12 mx-auto">
            <div className="summary-card p-4 rounded-3 shadow-sm">
              <h6 className="fw-bold text-uppercase mb-3">Order Summary</h6>

              <div className="d-flex flex-column gap-3">
                {cart.map((item) => {
                  const qty = item.qty ?? 1;
                  const line = Number(item.price || 0) * qty;
                  return (
                    <div className="d-flex align-items-center" key={item.id || item.title}>
                      <img
                        src={
                          item.coverUrl ||
                          item.images?.[1] ||
                          "https://via.placeholder.com/64x64?text=No+Image"
                        }
                        alt={item.title ?? "Product"}
                        width={64}
                        height={64}
                        style={{ objectFit: "cover", borderRadius: 8 }}
                        className="me-3"
                      />
                      <div className="flex-grow-1">
                        <div className="small fw-semibold">{item.title || item.name}</div>
                        {item.author && <div className="small text-muted">{item.author}</div>}
                      </div>
                      <div className="text-end small">
                        <div className="text-muted">×{qty}</div>
                        <div>{formatLKR(line)}</div>
                      </div>
                    </div>
                  );
                })}
              </div>

              <hr />

              <div className="d-flex justify-content-between small mb-2">
                <span className="text-muted">SUB TOTAL</span>
                <span>{formatLKR(subTotalAmount)}</span>
              </div>
              <div className="d-flex justify-content-between small mb-1">
                <span className="text-muted">
                  SHIPPING {shipLoading ? "(calculating…)" : ""}
                </span>
                <span>{formatLKR(shipping)}</span>
              </div>
              {shippingRule && (
                <div className="text-muted small mb-3">Rule: {shippingRule}</div>
              )}

              <div className="d-flex justify-content-between align-items-center">
                <span className="fw-bold">TOTAL</span>
                <span className="fw-bold fs-5">{formatLKR(grandTotal)}</span>
              </div>
            </div>

            {/* Submit button lives outside the form, so programmatically submit */}
            <button
              className="btn btn-dark w-100 mt-4 py-3"
              type="button"
              disabled={submitting || shipLoading}
              onClick={() => document.getElementById("checkout-form")?.requestSubmit()}
            >
              {submitting ? "Processing…" : `Pay Now — ${formatLKR(grandTotal)}`}
            </button>
          </div>
        </div>
      </div>

      <style>{`
        .summary-card { background: #f6f7f5; }
        .checkout-form .form-label { font-size: 0.9rem; color: #334; }
        .checkout-form input, .checkout-form select {
          background: #fff; border-radius: 10px;
        }
      `}</style>
    </>
  );
}
